import React from 'react';
import { ITheme, Stack, Text } from '@fluentui/react';
import { SreValidationDetail } from '../../../../../AGSS/models/SreHealth';
import { CopyButton } from './CopyButton';
import {
    isHashEntry,
    uniqueFindingEntries,
    uniqueHashingFailures,
} from './findingFormat';

interface ISreValidationFindingsProps {
    detail: SreValidationDetail;
    theme: ITheme;
    compact?: boolean;
}

const FILES_SHOWN = 25;

const fileListStyles = (theme: ITheme, compact: boolean) => ({
    root: {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: theme.palette.neutralPrimary,
        maxHeight: compact ? '160px' : '240px',
        overflowY: 'auto' as const,
        wordBreak: 'break-all' as const,
        whiteSpace: 'pre-wrap' as const,
        padding: compact ? '6px 8px' : '8px 12px',
        lineHeight: '20px',
        backgroundColor: theme.palette.neutralLighterAlt,
        borderRadius: 0,
    },
});

/**
 * Structured validation findings, with content-addressed entries formatted as Build/<hash>.
 */

export const SreValidationFindings: React.FC<ISreValidationFindingsProps> = ({
    detail,
    theme,
    compact = true,
}) => {
    const groups = detail.groups.map((group) => {
        const files = uniqueFindingEntries(group.files);
        return {
            ...group,
            files,
            isHashGroup: files.length > 0 && files.every(isHashEntry),
        };
    });
    const failures = uniqueHashingFailures(detail.hashingFailures);

    return (
        <Stack
            tokens={{ childrenGap: compact ? 8 : 12 }}
            styles={{ root: { minWidth: 0 } }}
        >
            {groups.map((group) => {
                const { files, isHashGroup } = group;

                return (
                    <Stack
                        key={group.name}
                        tokens={{ childrenGap: 6 }}
                        styles={{ root: { minWidth: 0 } }}
                    >
                        <Stack
                            horizontal
                            verticalAlign="center"
                            horizontalAlign="space-between"
                            tokens={{ childrenGap: 8 }}
                        >
                            <Text
                                as="h5"
                                variant="small"
                                styles={{
                                    root: {
                                        color: theme.palette.neutralSecondary,
                                        margin: 0,
                                        minWidth: 0,
                                        overflowWrap: 'anywhere',
                                        fontWeight: 600,
                                    },
                                }}
                            >
                                {group.name} ({files.length})
                                {isHashGroup
                                    ? ' — unique file hashes'
                                    : ' — unique entries'}
                                {group.truncated ? ' shown' : ''}
                            </Text>
                            <CopyButton
                                size="medium"
                                value={files.join('\n')}
                                label={
                                    isHashGroup
                                        ? `${group.name.toLowerCase()} hashes`
                                        : `${group.name.toLowerCase()} file list`
                                }
                            />
                        </Stack>
                        <Text
                            as="div"
                            role="region"
                            aria-label={`${group.name} entries`}
                            tabIndex={0}
                            variant="small"
                            styles={fileListStyles(theme, compact)}
                        >
                            {(compact
                                ? files.slice(0, FILES_SHOWN)
                                : files
                            ).map((file) => (
                                <div key={file}>{file}</div>
                            ))}
                            {compact && files.length > FILES_SHOWN && (
                                <div>
                                    {files.length - FILES_SHOWN} more entries
                                </div>
                            )}
                        </Text>
                        {group.truncated && (
                            <Text
                                variant="small"
                                styles={{
                                    root: {
                                        color: theme.palette.neutralSecondary,
                                    },
                                }}
                            >
                                Server capped this list; {group.count} entries
                                were found in total.
                            </Text>
                        )}
                    </Stack>
                );
            })}

            {failures.length > 0 && (
                <Stack
                    tokens={{ childrenGap: 6 }}
                    styles={{ root: { minWidth: 0 } }}
                >
                    <Stack
                        horizontal
                        verticalAlign="center"
                        horizontalAlign="space-between"
                        tokens={{ childrenGap: 8 }}
                    >
                        <Text
                            as="h5"
                            variant="small"
                            styles={{
                                root: {
                                    color: theme.palette.neutralSecondary,
                                    margin: 0,
                                    minWidth: 0,
                                    overflowWrap: 'anywhere',
                                    fontWeight: 600,
                                },
                            }}
                        >
                            Hashing failures ({failures.length}) — unique
                            entries
                            {detail.truncated ? ' shown' : ''}
                        </Text>
                        <CopyButton
                            size="medium"
                            value={failures
                                .map(
                                    (failure) =>
                                        `${failure.file}${
                                            failure.reason
                                                ? ` - ${failure.reason}`
                                                : ''
                                        }`
                                )
                                .join('\n')}
                            label="hashing failure list"
                        />
                    </Stack>
                    <Text
                        as="div"
                        role="region"
                        aria-label="Hashing failure entries"
                        tabIndex={0}
                        variant="small"
                        styles={fileListStyles(theme, compact)}
                    >
                        {(compact
                            ? failures.slice(0, FILES_SHOWN)
                            : failures
                        ).map((failure) => (
                            <div key={failure.file}>
                                {failure.file}
                                {failure.reason ? ` — ${failure.reason}` : ''}
                            </div>
                        ))}
                        {compact && failures.length > FILES_SHOWN && (
                            <div>
                                {failures.length - FILES_SHOWN} more entries
                            </div>
                        )}
                    </Text>
                </Stack>
            )}
            {detail.truncated && (
                <Text
                    variant="small"
                    styles={{ root: { color: theme.palette.neutralSecondary } }}
                >
                    Partial validation preview
                </Text>
            )}
        </Stack>
    );
};
